import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  Clock,
  Package,
  Lightbulb,
  Download,
  MessageCircle,
  CheckCircle2,
  Sparkles,
} from "lucide-react";
import {
  fetchDailyActivity,
  fetchDailyList,
  dailyWorksheetPdfUrl,
} from "../lib/dailyApi";
import { useAuth } from "../contexts/AuthContext";

/**
 * DailyActivity — public, no-login page rendering one day's full activity:
 * header, instructions, time/materials, steps, teacher tip, per-grade
 * adaptations, debrief question, downloadable worksheet, upgrade CTA, and
 * previous/next navigation.
 *
 * URL: /daily/day-:dayNum (matched in App.js)
 */
export default function DailyActivity() {
  const params = useParams();
  const { user } = useAuth();
  // URL is /daily/day-<n>. Parse the trailing number out of the slug so we
  // can keep the spec'd URL pattern while staying compatible with React
  // Router v6's single-segment param rule.
  const match = (params.slug || "").match(/^day-(\d+)$/i);
  const day = match ? parseInt(match[1], 10) : NaN;

  const [activity, setActivity] = useState(null);
  const [allDays, setAllDays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!Number.isFinite(day) || day < 1 || day > 12) {
      setNotFound(true);
      setLoading(false);
      return;
    }
    setLoading(true);
    setNotFound(false);
    Promise.all([fetchDailyActivity(day), fetchDailyList()])
      .then(([a, list]) => {
        setActivity(a);
        // list comes back newest first — sort ascending for nav use
        const sorted = (list.activities || [])
          .slice()
          .sort((x, y) => x.day - y.day);
        setAllDays(sorted);
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
    // scroll to top when changing day
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [day]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f4f5f7] grid place-items-center text-[#4a6984]">
        Loading activity…
      </div>
    );
  }

  if (notFound || !activity) {
    return (
      <div
        className="min-h-screen bg-[#f4f5f7] grid place-items-center px-6 text-center"
        data-testid="daily-activity-not-found"
      >
        <div className="max-w-md">
          <h1 className="font-black text-2xl text-[#1c2d3d]">
            That activity doesn&apos;t exist.
          </h1>
          <p className="mt-3 text-[#4a6984] font-medium">
            Try the index of all 12 daily activities.
          </p>
          <Link
            to="/daily"
            className="mt-5 inline-flex items-center gap-2 px-5 py-3 rounded-full text-sm font-extrabold bg-[#96ded1] text-[#1c2d3d] hover:bg-white border border-[#96ded1]"
          >
            See all activities <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      </div>
    );
  }

  const prevDay = allDays.find((a) => a.day === day - 1);
  const nextDay = allDays.find((a) => a.day === day + 1);
  const worksheetUrl = dailyWorksheetPdfUrl(day);

  return (
    <div
      className="min-h-screen bg-[#f4f5f7] text-[#1c2d3d]"
      data-testid={`daily-activity-day-${day}`}
    >
      {/* Header bar */}
      <header className="bg-[#1c2d3d] text-white">
        <div className="max-w-4xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link
            to="/"
            data-testid="daily-activity-home-link"
            className="inline-flex items-center gap-2 font-extrabold tracking-tight hover:opacity-80"
          >
            <img
              src="/brand/atomicedu-logo-mark.png"
              alt="Atomic Edu"
              className="h-9 w-9 object-contain"
            />
            Atomic Edu
          </Link>
          <Link
            to="/daily"
            data-testid="daily-activity-index-link"
            className="text-xs font-bold uppercase tracking-[0.18em] text-white/85 hover:text-white"
          >
            All 12 activities →
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-12">
        {/* Hero */}
        <div className="inline-flex items-center gap-2 rounded-full bg-[#1c2d3d] text-white px-3 py-1 text-[10px] font-black uppercase tracking-[0.2em]">
          Day {activity.day} · {activity.skill}
        </div>
        <h1
          data-testid="daily-activity-title"
          className="mt-5 font-black text-4xl sm:text-5xl text-[#1c2d3d] tracking-tighter leading-[1.05]"
        >
          {activity.title}
        </h1>
        <p
          data-testid="daily-activity-hook"
          className="mt-4 text-lg text-[#4a6984] font-medium leading-relaxed"
        >
          {activity.description}
        </p>

        {/* Time + Materials chips */}
        <div className="mt-6 flex flex-wrap gap-3">
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold bg-white border border-[#4a6984]/20 text-[#1c2d3d]">
            <Clock className="h-3.5 w-3.5" /> {activity.time_estimate}
          </span>
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold bg-white border border-[#4a6984]/20 text-[#1c2d3d]">
            <Package className="h-3.5 w-3.5" /> Materials: {activity.materials}
          </span>
        </div>

        {/* How it works */}
        <section
          data-testid="daily-activity-steps"
          className="mt-12 rounded-2xl border border-[#4a6984]/15 bg-white p-7 md:p-8 shadow-sm"
        >
          <h2 className="font-black text-xl text-[#1c2d3d] tracking-tight">
            How it works
          </h2>
          <ol className="mt-5 space-y-4">
            {activity.steps.map((step, i) => (
              <li
                key={i}
                data-testid={`daily-step-${i + 1}`}
                className="flex gap-4"
              >
                <span className="shrink-0 h-7 w-7 rounded-full bg-[#96ded1] text-[#1c2d3d] grid place-items-center font-black text-sm">
                  {i + 1}
                </span>
                <p className="text-[#4a6984] font-medium leading-relaxed pt-0.5">
                  {step}
                </p>
              </li>
            ))}
          </ol>
        </section>

        {/* Teacher tip */}
        <section
          data-testid="daily-activity-tip"
          className="mt-6 rounded-2xl border-l-[4px] border-l-[#96ded1] border border-[#4a6984]/15 bg-white p-6"
        >
          <div className="flex items-start gap-3">
            <Lightbulb className="h-5 w-5 text-[#1c2d3d] shrink-0 mt-0.5" />
            <div>
              <p className="font-bold uppercase text-xs tracking-[0.25em] text-[#4a6984]/70">
                Teacher Tip
              </p>
              <p className="mt-2 text-[#1c2d3d] font-medium leading-relaxed">
                {activity.teacher_tip}
              </p>
            </div>
          </div>
        </section>

        {/* Grade adaptations */}
        <section
          data-testid="daily-activity-grades"
          className="mt-12"
        >
          <h2 className="font-black text-xl text-[#1c2d3d] tracking-tight">
            Adapt it for your grade
          </h2>
          <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
            {["4", "5", "6", "7", "8"].map((g) => (
              <div
                key={g}
                data-testid={`daily-grade-${g}`}
                className="rounded-xl border border-[#4a6984]/15 bg-white p-5"
              >
                <p className="font-black text-sm text-[#1c2d3d] tracking-tight">
                  Grade {g}
                </p>
                <p className="mt-2 text-[13px] text-[#4a6984] font-medium leading-relaxed">
                  {activity.grade_adaptations[g]}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* Debrief question */}
        <section
          data-testid="daily-activity-debrief"
          className="mt-12 rounded-2xl bg-[#1c2d3d] text-white p-7 md:p-8"
        >
          <div className="flex items-start gap-3">
            <MessageCircle className="h-5 w-5 text-[#96ded1] shrink-0 mt-1" />
            <div>
              <p className="font-bold uppercase text-xs tracking-[0.25em] text-[#96ded1]">
                Debrief question
              </p>
              <p className="mt-3 font-bold text-lg md:text-xl leading-relaxed">
                {activity.debrief_question}
              </p>
            </div>
          </div>
        </section>

        {/* Worksheet download */}
        <section
          data-testid="daily-activity-worksheet"
          className="mt-12 rounded-2xl border border-[#4a6984]/15 bg-white p-7 md:p-8"
        >
          <h2 className="font-black text-xl text-[#1c2d3d] tracking-tight">
            Printable student worksheet
          </h2>
          <p className="mt-3 text-[#4a6984] font-medium leading-relaxed">
            One page. Clean layout. Free, no login.
          </p>
          <a
            href={worksheetUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="daily-worksheet-download"
            className="mt-5 inline-flex items-center gap-2 px-5 py-3 rounded-full text-sm font-extrabold bg-[#96ded1] text-[#1c2d3d] hover:bg-white border border-[#96ded1] shadow-md hover:shadow-lg transition-all hover:-translate-y-0.5"
          >
            <Download className="h-4 w-4" /> Download worksheet (PDF)
          </a>
        </section>

        {/* Upgrade prompt — when the teacher is signed in we drop them
            into the generator pre-filled with this activity's
            transferable skill (no need to upsell again). Signed-out
            visitors still see the trial pitch. */}
        <section
          data-testid="daily-activity-upgrade"
          className="mt-12 rounded-2xl border border-[#96ded1]/40 bg-gradient-to-br from-white via-white to-[#96ded1]/10 p-7 md:p-9"
        >
          <h2 className="font-black text-2xl text-[#1c2d3d] tracking-tight">
            Want the full lesson?
          </h2>
          <p className="mt-3 text-[#4a6984] font-medium leading-relaxed">
            The complete version includes a teacher script, slide deck, exit
            ticket, and observation prompts — ready to use tomorrow.
          </p>
          {user ? null : (
            <p className="mt-2 text-[#4a6984] font-medium leading-relaxed">
              It&apos;s free for 7 days. No credit card needed.
            </p>
          )}
          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            {user ? (
              <Link
                to={`/generator?skill=${encodeURIComponent(activity.skill)}`}
                data-testid="daily-activity-cta-build"
                className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full text-sm font-extrabold bg-[#96ded1] text-[#1c2d3d] hover:bg-white border border-[#96ded1] shadow-md hover:shadow-lg transition-all hover:-translate-y-0.5"
              >
                <Sparkles className="h-4 w-4" /> Build the full lesson
                <ArrowRight className="h-4 w-4" />
              </Link>
            ) : (
              <>
                <Link
                  to="/pricing"
                  data-testid="daily-activity-cta-trial"
                  className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full text-sm font-extrabold bg-[#96ded1] text-[#1c2d3d] hover:bg-white border border-[#96ded1] shadow-md hover:shadow-lg transition-all hover:-translate-y-0.5"
                >
                  <CheckCircle2 className="h-4 w-4" /> Start your free trial
                </Link>
                <Link
                  to="/pricing"
                  data-testid="daily-activity-cta-signin"
                  className="inline-flex items-center justify-center gap-2 text-sm font-bold text-[#4a6984] hover:text-[#1c2d3d] underline-offset-4 hover:underline"
                >
                  Already have an account? Sign in
                </Link>
              </>
            )}
          </div>
        </section>

        {/* Prev / Next nav */}
        <nav
          data-testid="daily-activity-nav"
          className="mt-16 pt-8 border-t border-[#4a6984]/15 flex items-center justify-between gap-4"
        >
          {prevDay ? (
            <Link
              to={`/daily/day-${prevDay.day}`}
              data-testid="daily-activity-prev"
              className="inline-flex items-center gap-2 text-sm font-extrabold text-[#1c2d3d] hover:text-[#4a6984]"
            >
              <ArrowLeft className="h-4 w-4" />
              <span>
                <span className="block text-[10px] uppercase tracking-[0.18em] text-[#4a6984]/70 font-bold">
                  Previous
                </span>
                <span>Day {prevDay.day}: {prevDay.title}</span>
              </span>
            </Link>
          ) : (
            <span />
          )}
          {nextDay ? (
            <Link
              to={`/daily/day-${nextDay.day}`}
              data-testid="daily-activity-next"
              className="inline-flex items-center gap-2 text-sm font-extrabold text-[#1c2d3d] hover:text-[#4a6984] text-right"
            >
              <span>
                <span className="block text-[10px] uppercase tracking-[0.18em] text-[#4a6984]/70 font-bold">
                  Next
                </span>
                <span>Day {nextDay.day}: {nextDay.title}</span>
              </span>
              <ArrowRight className="h-4 w-4" />
            </Link>
          ) : (
            <span />
          )}
        </nav>
      </main>
    </div>
  );
}
